import { clusterColor } from "@/lib/cluster-colors";
import type { LandscapePaper } from "@/types/landscape";

export interface ClusterSummary {
  id: number;
  label: string;
  count: number;
  /** Mean normalized_score over papers with a positive score; 0 if none scored. */
  avgScore: number;
  color: string;
  /** Centroid of the cluster's papers in landscape data-space (for zoom-to-region). */
  centroid: { x: number; y: number };
}

/**
 * Summarize the displayed landscape papers by topic cluster.
 *
 * Lifted from the retired Concept Map's cluster-summary logic. Operates on the
 * papers actually on the canvas (the Landscape payload is capped at the top-500
 * by score), so counts describe the displayed dots, not the full corpus —
 * consistent with what the user sees. Unclustered (id < 0) sorts last.
 */
export function summarizeClusters(papers: LandscapePaper[]): ClusterSummary[] {
  const groups = new Map<
    number,
    {
      label: string;
      count: number;
      scoreSum: number;
      scoreCount: number;
      xSum: number;
      ySum: number;
    }
  >();

  for (const p of papers) {
    const id = p.topic_cluster_id ?? -1;
    const scored = p.normalized_score > 0;
    const existing = groups.get(id);
    if (existing) {
      existing.count++;
      existing.xSum += p.x;
      existing.ySum += p.y;
      if (scored) {
        existing.scoreSum += p.normalized_score;
        existing.scoreCount++;
      }
    } else {
      groups.set(id, {
        label: p.topic_cluster_label || (id < 0 ? "Unclustered" : `Cluster ${id}`),
        count: 1,
        scoreSum: scored ? p.normalized_score : 0,
        scoreCount: scored ? 1 : 0,
        xSum: p.x,
        ySum: p.y,
      });
    }
  }

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a < 0) return 1;
      if (b < 0) return -1;
      return a - b;
    })
    .map(([id, g]) => ({
      id,
      label: g.label,
      count: g.count,
      avgScore: g.scoreCount > 0 ? Math.round(g.scoreSum / g.scoreCount) : 0,
      color: clusterColor(id),
      centroid: { x: g.xSum / g.count, y: g.ySum / g.count },
    }));
}
